import { fetchEmployeeRegList } from '@api/useQueryCalls';
import PointEarned from '@atoms/UserProfile/PointEarned';
import { Skeleton, SkeletonText, Center, VStack, Text } from '@components/primitives';
import { Fontisto, Ionicons } from '@expo/vector-icons';
import { useToast } from '@hooks';
import { shouldShowError } from '@organisms/HomeTab/homeUtils';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import generateCodeShortLink from '@screens/GGReferralPage/referralPageUtils';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import { scale } from '@utils/normalize';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, Pressable, TextInput, StatusBar, ScrollView, Platform } from 'react-native';
import { SceneMap, TabBar, TabView } from 'react-native-tab-view';
import {
  logEmployeeList,
  logInputChange,
  logInviteStatusClicked,
  logPicPage,
  logTermsPress,
} from './analyticsPIcListing';
import { getReferralEncryption, calculateTotalPoints } from './picListingUtils';
import styles from './style';
import NamesListing from '../NameListing';

const renderScene = SceneMap({
  daftarTeman: NamesListing,
  statusAjakan: NamesListing,
});

export const renderTabBar = (props: any) => (
  <TabBar
    {...props}
    testID="tabBar"
    getLabelText={({ route }: { route: { title: string } }) => route.title}
    style={styles.tabBarStyle}
    activeTintColor={colors.neutral.cotton}
    inactiveTintColor={colors.secondary.orchid}
    tabStyle={styles.tabStyle}
    labelStyle={styles.labelStyle}
    inactiveColor={styles.inActiveTabbarStyle}
    indicatorStyle={styles.indicatorStyle}
    indicatorContainerStyle={styles.indicatorContainerStyle}
  />
);

export const onPageChangeHandler = (idx: number, setIndex: (index: number) => void, isPIC: boolean): void => {
  if (idx === 0) logEmployeeList(isPIC);
  if (idx === 1) logInviteStatusClicked(isPIC);
  setIndex(idx);
};

export function shouldRenderClose(show: boolean, setSearchText: (text: string) => void): JSX.Element | null {
  if (show)
    return (
      <Pressable testID="closeSearchBtn" onPress={() => setSearchText('')}>
        <Ionicons name="close-circle" color={colors.neutral.charcoal} size={scale(16)} style={styles.closeStyle} />
      </Pressable>
    );
  return null;
}

export const SkeletonLoader = (): JSX.Element => (
  <Center width="100%" flex={1}>
    <VStack width="100%" space={8} overflow="hidden" rounded="md">
      <Skeleton height={40} />
      <SkeletonText paddingX={4} />
      <SkeletonText paddingX={4} />
      <SkeletonText paddingX={4} />
      <SkeletonText paddingX={4} />
      <SkeletonText paddingX={4} />
      <SkeletonText paddingX={4} />
    </VStack>
  </Center>
);

const PicLanding = () => {
  const [index, setIndex] = useState<number>(0);

  const {
    setSearchText,
    setPicReferralEmployees,
    employeeId,
    searchText,
    resetPicReferral,
    setInitialData,
    employerId,
  } = getStore();

  const referralCode = getReferralEncryption(employeeId?.toString());

  const navigation = useNavigation<any>();
  const [accumulatedTotalPoin, setAccumulatedTotalPoin] = useState<number>(0);
  const { t } = useTranslation('common');

  const tabOneMeta = { key: 'daftarTeman', title: t('userProfile.titleOne'), shortLink: '' };
  const tabTwoMeta = {
    key: 'statusAjakan',
    title: t('userProfile.titleTwo'),
    shortLink: '',
  };

  const [routes, setRoutes] = useState<(typeof tabOneMeta)[]>([tabOneMeta, tabTwoMeta]);
  const toast = useToast();

  const { employeeProfile } = getStore();

  const isPIC = employeeProfile?.influencerRole && employeeProfile?.influencerRole === 'PIC';

  const {
    isLoading: empRegLoader,
    isError: isEmpRegError,
    error: empRegError,
    refetch: fetchEmployeeRegListCall,
  } = fetchEmployeeRegList({ employeeId }, (data: any) => {
    setAccumulatedTotalPoin(calculateTotalPoints(data));
    setPicReferralEmployees(data?.employeesToRefer ?? [], data?.employeesReferred ?? []);
  });

  useEffect(() => {
    shouldShowError(isEmpRegError, empRegError, toast, t, navigation, setInitialData);
  }, [isEmpRegError, empRegError]);

  useEffect(() => {
    logPicPage(isPIC);
  }, []);

  const getShareLink = () => {
    try {
      const campaign_source = `whatsapp`;
      const campaign_medium = `pic_zone_referral`;
      const campaign_name = `emplr_${employerId}`;
      const utm_term = Platform.OS === 'web' ? 'pwa_app' : 'mobile_app';
      const utm_content = 'pic_zone_referral';
      const shortLink = generateCodeShortLink(
        referralCode,
        campaign_source,
        campaign_medium,
        campaign_name,
        utm_term,
        utm_content,
      );

      setRoutes([
        { ...tabOneMeta, shortLink },
        { ...tabTwoMeta, shortLink },
      ]);
    } catch (e) {
      // console.log(e)
    }
  };

  const isFocused = useIsFocused();

  useEffect(() => () => resetPicReferral(), []);

  useEffect(() => {
    getShareLink();
  }, []);

  useEffect(() => {
    if (isFocused) fetchEmployeeRegListCall();
  }, [isFocused]);

  useEffect(() => {
    setSearchText('');
  }, [setSearchText]);

  const onTermsPress = () => {
    logTermsPress(isPIC);
    navigation.navigate('PicTerms');
  };

  const onInputEndHandler = () => {
    logInputChange(isPIC);
  };

  if (empRegLoader) return <SkeletonLoader />;

  return (
    <ScrollView contentContainerStyle={styles.scrollableContainerStyle} style={styles.mainContentStyle}>
      <StatusBar translucent={false} backgroundColor={colors.secondary.orchid} />

      <PointEarned accumulatedTotalPoin={accumulatedTotalPoin} t={t} isPIC={isPIC} />
      <Text variant="sm-normal" style={styles.termsParentLink}>
        {t('userProfile.termsText')}
        <Text variant="sm-bold" testID="linkTerms" onPress={onTermsPress} style={styles.linkStyle}>
          {' '}
          {t('userProfile.linkTerms')}
        </Text>
      </Text>
      <View style={styles.inputTextContainer}>
        <TextInput
          value={searchText}
          testID="test-id"
          style={styles.inputSearch}
          onBlur={onInputEndHandler}
          onChangeText={(tx: string) => setSearchText(tx)}
          placeholder={t('userProfile.textInputPlaceHolder')}
          placeholderTextColor={colors.neutral.darkMist}
        />
        {shouldRenderClose(!!searchText?.length, setSearchText)}
        <Fontisto name="search" color={colors.neutral.charcoal} size={scale(16)} />
      </View>
      <View style={styles.tabViewContainer}>
        <TabView
          lazy
          testID="tabViewPic"
          renderTabBar={renderTabBar}
          navigationState={{ index, routes }}
          renderScene={renderScene}
          onIndexChange={(idx: number) => onPageChangeHandler(idx, setIndex, isPIC)}
        />
      </View>
    </ScrollView>
  );
};

export default PicLanding;
